import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './contexts/ThemeContext';
import { TourProvider } from './contexts/TourContext';
import { AuthProvider } from './contexts/AuthContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SuspenseFallback, PageSuspenseFallback } from './components/SuspenseFallback';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';

// Eagerly loaded components (critical for initial render)
import { DashboardLayoutWrapper } from './components/layout/DashboardLayoutWrapper';
import { LoginPage } from './pages/auth/LoginPage';
import { AdminLoginPage } from './pages/auth/AdminLoginPage';

// Lazy loaded components (code splitting)
const WelcomeTourModal = lazy(() => import('./components/ui/WelcomeTourModal').then(m => ({ default: m.WelcomeTourModal })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage').then(m => ({ default: m.ApplicationsPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const CreateApplicationPage = lazy(() => import('./pages/CreateApplicationPage').then(m => ({ default: m.CreateApplicationPage })));
const ApplicationDetailPage = lazy(() => import('./pages/ApplicationDetailPage').then(m => ({ default: m.ApplicationDetailPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const NotFoundPage = lazy(() => import('./pages/errors/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const ForbiddenPage = lazy(() => import('./pages/errors/ForbiddenPage').then(m => ({ default: m.ForbiddenPage })));
const AccountLockedPage = lazy(() => import('./pages/errors/AccountLockedPage').then(m => ({ default: m.AccountLockedPage })));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminDashboard })));
const UserManagement = lazy(() => import('./pages/admin').then(m => ({ default: m.UserManagement })));
const DepartmentManagement = lazy(() => import('./pages/admin').then(m => ({ default: m.DepartmentManagement })));
const CategoryManagement = lazy(() => import('./pages/admin').then(m => ({ default: m.CategoryManagement })));
const AdminSettings = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminSettings })));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <FavoritesProvider>
                <TourProvider>
                  <Router>
                    <Suspense fallback={<SuspenseFallback />}>
                    <Routes>
                      {/* Default route - redirect to login */}
                      <Route path="/" element={<Navigate to="/login" replace />} />
                    
                    {/* Auth Routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/admin/login" element={<AdminLoginPage />} />
                    <Route path="/forgot-password" element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <ForgotPasswordPage />
                      </Suspense>
                    } />
                    
                    {/* Protected Routes - wrapped in single DashboardLayout */}
                    <Route element={
                      <ProtectedRoute>
                        <>
                          <Suspense fallback={null}>
                            <WelcomeTourModal />
                          </Suspense>
                          <DashboardLayoutWrapper />
                        </>
                      </ProtectedRoute>
                    }>
                      <Route path="/dashboard" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <Dashboard />
                        </Suspense>
                      } />
                      <Route path="/applications" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <ApplicationsPage />
                        </Suspense>
                      } />
                      <Route path="/applications/new" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <CreateApplicationPage />
                        </Suspense>
                      } />
                      <Route path="/applications/:id" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <ApplicationDetailPage />
                        </Suspense>
                      } />
                      <Route path="/analytics" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <AnalyticsPage />
                        </Suspense>
                      } />
                      <Route path="/reports" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <ReportsPage />
                        </Suspense>
                      } />
                      <Route path="/profile" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <ProfilePage />
                        </Suspense>
                      } />
                      <Route path="/settings" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <SettingsPage />
                        </Suspense>
                      } />
                      <Route path="/notifications" element={
                        <Suspense fallback={<PageSuspenseFallback />}>
                          <NotificationsPage />
                        </Suspense>
                      } />
                      
                      {/* Admin Routes */}
                      <Route path="/admin" element={
                        <ProtectedAdminRoute>
                          <Suspense fallback={<PageSuspenseFallback />}>
                            <AdminDashboard />
                          </Suspense>
                        </ProtectedAdminRoute>
                      } />
                      <Route path="/admin/users" element={
                        <ProtectedAdminRoute>
                          <Suspense fallback={<PageSuspenseFallback />}>
                            <UserManagement />
                          </Suspense>
                        </ProtectedAdminRoute>
                      } />
                      <Route path="/admin/departments" element={
                        <ProtectedAdminRoute>
                          <Suspense fallback={<PageSuspenseFallback />}>
                            <DepartmentManagement />
                          </Suspense>
                        </ProtectedAdminRoute>
                      } />
                      <Route path="/admin/categories" element={
                        <ProtectedAdminRoute>
                          <Suspense fallback={<PageSuspenseFallback />}>
                            <CategoryManagement />
                          </Suspense>
                        </ProtectedAdminRoute>
                      } />
                      <Route path="/admin/settings" element={
                        <ProtectedAdminRoute>
                          <Suspense fallback={<PageSuspenseFallback />}>
                            <AdminSettings />
                          </Suspense>
                        </ProtectedAdminRoute>
                      } />
                    </Route>
                    
                    {/* Error Pages */}
                    <Route path="/errors/forbidden" element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <ForbiddenPage />
                      </Suspense>
                    } />
                    <Route path="/errors/account-locked" element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <AccountLockedPage />
                      </Suspense>
                    } />
                    <Route path="/errors/404" element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <NotFoundPage />
                      </Suspense>
                    } />
                    
                    {/* Catch-all for 404 */}
                    <Route path="*" element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <NotFoundPage />
                      </Suspense>
                    } />
                  </Routes>
                </Suspense>
              </Router>
            </TourProvider>
            </FavoritesProvider>
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
